import { useCallback } from 'react';
import { getUserService } from '../service/ServiceRegistry';
import type { FetchDataFn, TableQueryParams, TableDataResponse } from '../types/table';
import type { 
  AdminUser, 
  AdminUsersQueryParams, 
  AdminUserDetail,
  UserUpdateRequest 
} from '../types/api/admin-user';

/**
 * Custom hook for admin user management
 * 
 * Integrates UserService with the DataTable component
 * Handles data transformation between API and table formats
 * 
 * Features:
 * - Automatic data fetching
 * - Filter handling
 * - Pagination conversion (DataTable uses 1-based, API uses 0-based)
 * - Error handling
 * - Type safety
 */
export function useAdminUsers() {
  const userService = getUserService();

  /**
   * Fetch function compatible with DataTable
   * Transforms DataTable params to API params and response back to DataTable format
   */
  const fetchAdminUsers: FetchDataFn<AdminUser> = useCallback(
    async (params: TableQueryParams): Promise<TableDataResponse<AdminUser>> => {
      console.log('fetchAdminUsers called with params:', params);
      try {
        // API now expects 1-based page index; pass through directly
        const apiParams: AdminUsersQueryParams = {
          page: params.page,
          size: params.pageSize,
        };

        // Add search parameter if provided
        if (params.search) {
          // The API doesn't have a unified search, so we'll search by name
          apiParams.name = params.search;
        }

        // Add server-side filters
        if (params.filters) {
          if (params.filters.tier?.value) {
            apiParams.tier = params.filters.tier.value;
          }
          if (params.filters.email?.value) {
            apiParams.email = params.filters.email.value;
          }
          if (params.filters.phone?.value) {
            apiParams.phone = params.filters.phone.value;
          }
          if (params.filters.registration_date_from?.value) {
            apiParams.registration_date_from = params.filters.registration_date_from.value;
          }
          if (params.filters.registration_date_to?.value) {
            apiParams.registration_date_to = params.filters.registration_date_to.value;
          }
        }

        // Fetch data from API
        const response: any = await userService.getAdminUsers(apiParams);
        console.log('response from admin users hook', response);

        // Gender filter is not supported by the API — apply client-side
        const genderFilter = params.filters?.gender?.value;
        const items: AdminUser[] = genderFilter
          ? response.items.filter((user: AdminUser) => user.gender === genderFilter)
          : response.items;

        return {
          data: items,
          pagination: {
            page: response.page, 
            pageSize: response.size,
            total: genderFilter ? items.length : response.totalItems,
            totalPages: genderFilter ? 1 : response.totalPages,
            hasNext: genderFilter ? false : response.hasNext,
            hasPrev: genderFilter ? false : response.hasPrevious,
          },
        };
      } catch (error) {
        console.error('Failed to fetch admin users:', error);
        throw new Error('Failed to load users. Please try again.');
      }
    },
    [userService]
  );

  return {
    fetchAdminUsers,
  };
}

/**
 * Hook for user actions
 * Provides functions to perform actions on users
 */
export function useAdminUserActions() {
  const userService = getUserService();

  /**
   * Get user detail by ID
   */
  const getUserDetail = useCallback(
    async (userId: string): Promise<AdminUserDetail> => {
      try {
        return await userService.getAdminUserDetail(userId);
      } catch (error) {
        console.error('Failed to fetch user detail:', error);
        throw error;
      }
    },
    [userService]
  );

  /**
   * Update user with optional profile image
   */
  const updateUser = useCallback(
    async (
      userId: string, 
      updateData: UserUpdateRequest, 
      profileImage?: File | null
    ): Promise<AdminUserDetail> => {
      try {
        return await userService.updateAdminUser(userId, updateData, profileImage);
      } catch (error) {
        console.error('Failed to update user:', error);
        throw error;
      }
    },
    [userService]
  );

  /**
   * Soft delete user with reason
   */
  const softDeleteUser = useCallback(
    async (userId: string, reason: string): Promise<void> => {
      try {
        await userService.softDeleteUser(userId, reason);
      } catch (error) {
        console.error('Failed to delete user:', error);
        throw error;
      }
    },
    [userService]
  );

  /**
   * Restore soft-deleted user
   */
  const restoreUser = useCallback(
    async (userId: string): Promise<void> => {
      try {
        await userService.restoreUser(userId);
      } catch (error) {
        console.error('Failed to restore user:', error);
        throw error;
      }
    },
    [userService]
  );

  /**
   * Check if user is deleted
   */
  const checkUserDeletedStatus = useCallback(
    async (userId: string): Promise<boolean> => {
      try {
        return await userService.checkUserDeletedStatus(userId);
      } catch (error) {
        console.error('Failed to check user deleted status:', error);
        throw error;
      }
    },
    [userService]
  );

  /**
   * Legacy method - kept for backward compatibility
   * Use softDeleteUser instead
   */
  const deleteUser = useCallback(
    async (userId: string, reason: string): Promise<void> => {
      return softDeleteUser(userId, reason);
    },
    [softDeleteUser]
  );

  /**
   * Suspend user account
   */
  const suspendUser = useCallback(
    async (user: AdminUser) => {
      try {
        await userService.suspendUser(user.userId);
      } catch (error) {
        console.error('Failed to suspend user:', error);
        throw error;
      }
    },
    [userService]
  );

  /**
   * Export users to CSV
   */
  const exportUsers = useCallback(async () => {
    try {
      console.log('Exporting users...');
      // TODO: Implement user export
      alert('Export functionality coming soon!');
    } catch (error) {
      console.error('Failed to export users:', error);
      throw error;
    }
  }, []);

  return {
    getUserDetail,
    updateUser,
    softDeleteUser,
    restoreUser,
    checkUserDeletedStatus,
    deleteUser, // Legacy
    suspendUser,
    exportUsers,
  };
}

